import { CanvasWordTokenElement } from './CanvasWordTokenElement';

/**
 * A WordToken contains a single word, which can be divided in many independently
 * highlightable CanvasWordTokenElements.
 *
 */
export class CanvasWordToken {
    #wordElementTokens: CanvasWordTokenElement[] = [];

    /**
     * Builds a WordToken with a given text and highlight status.
     */
    constructor(text?: string, highlighted: boolean = false, sourceWordReferenceIndex?: number) {
        if (text) {
            this.#wordElementTokens.push(
                new CanvasWordTokenElement(text, highlighted, sourceWordReferenceIndex),
            );
        }
    }

    /**
     * Text contained in all CanvasWordTokenElements of this word.
     */
    get text(): string {
        return this.#wordElementTokens.reduce(
            (result, wordElementToken) => result + wordElementToken.text,
            '',
        );
    }

    /**
     * Length of this word.
     */
    get length(): number {
        return this.#wordElementTokens.reduce(
            (result, wordElementToken) => result + wordElementToken.text.length,
            0,
        );
    }

    /**
     * Contains tokens which represent parts of a word.
     */
    get wordElementTokens(): CanvasWordTokenElement[] {
        return this.#wordElementTokens;
    }

    /**
     * Highlight a part of the token, potentially splitting wordElementTokens.
     */
    highlight(highlightStart: number, highlightEnd: number) {
        const newCanvasWordTokenElements: CanvasWordTokenElement[] = [];
        this.#wordElementTokens.forEach((wordElementToken) => {
            const length = wordElementToken.text.length;

            if (length > highlightStart && highlightEnd > 0) {
                // The token might have to be split in up to 3 tokens.

                // add normal part before highlight start (if any)
                if (highlightStart > 0 && length >= highlightStart) {
                    newCanvasWordTokenElements.push(
                        new CanvasWordTokenElement(
                            wordElementToken.text.substring(0, highlightStart),
                            false,
                        ),
                    );
                }

                // add highlighted part
                newCanvasWordTokenElements.push(
                    new CanvasWordTokenElement(
                        wordElementToken.text.substring(highlightStart, highlightEnd),
                        true,
                    ),
                );

                // add normal part after highlight end (if any)
                if (length - highlightEnd > 0) {
                    newCanvasWordTokenElements.push(
                        new CanvasWordTokenElement(
                            wordElementToken.text.substring(highlightEnd),
                            false,
                        ),
                    );
                }
            } else {
                newCanvasWordTokenElements.push(wordElementToken);
            }

            // decrement highlight bounds to keep them relative to the next element's start position
            highlightStart -= length;
            highlightEnd -= length;
        });

        this.#wordElementTokens = newCanvasWordTokenElements;
    }

    /**
     * Adds a CanvasWordTokenElement to the word.
     */
    add(wordElementToken: CanvasWordTokenElement) {
        this.#wordElementTokens.push(wordElementToken);
    }
}
